import React, { useEffect, useState } from "react";
import { TableCell, TableHead, TableRow, TableContainer, Table, TableBody, Toolbar, Typography } from "@mui/material";
import { Done, Edit, Undo, Add, Cancel, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AutomaticApprovalSettings, UserRoles } from "../../../Constant";
import AutomaticApprovalApiService from "../../../adapters/xhr/AutomaticApprovalApiService";
import AdministrationApiService from "../../../adapters/xhr/AdministrationApiService";
import authService from "../../../components/api-authorization/AuthorizeService";
import { MyDatePicker } from "../../../components/my-date-picker/MyDatePicker";
import { MyDropdown } from "../../../components/my-dropdown/MyDropdown";
import SnackbarUtils from "../../../SnackbarUtils";
import { formatDate } from "../../../Utils";

export const StopsToRemoveSection = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [previousRows, setPreviousRows] = useState([]);
  const [stops, setStops] = useState([]);
  const isReadOnly = authService._user?.role == UserRoles.CaseManager;
  const hasAddRow = rows?.some((item) => item.isAddMode) ?? false;
  const hasEditRow = rows?.some((item) => item.isEditMode) ?? false;

  const columns = [
    { id: "Value", label: t("stop"), minWidth: 400, width: 400, editable: true },
    { id: "FromDate", label: t("from_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "ToDate", label: t("to_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "Actions", label: t("actions"), minWidth: 100, hidden: isReadOnly },
  ];

  useEffect(() => {
    fetchSettingsAsync();
    fetchStopsAsync();
  }, []);

  const fetchSettingsAsync = async () => {
    const response = await AutomaticApprovalApiService.getAll();
    if (response?.isSuccess) {
      const data = response.data
        .filter((item) => item.key.startsWith(AutomaticApprovalSettings.StopsToRemove))
        .map((item) => ({
          id: item.id,
          Key: item.key,
          Value: item.value,
          FromDate: item.fromDate,
          ToDate: item.toDate,
          DataType: item.dataType,
        }));

      setRows(data);
      setPreviousRows(data);
    }
  };

  const fetchStopsAsync = async () => {
    const response = await AdministrationApiService.getStops();
    if (response?.isSuccess) {
      setStops(response.data.map((item) => item.stopAreaName));
    }
  };

  const onAddRow = () => {
    setRows([
      {
        id: "0",
        Key: AutomaticApprovalSettings.StopsToRemove,
        Value: "",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
      },
      ...previousRows,
    ]);
  };

  const onEditRow = (id) => {
    setRows(
      previousRows.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: true };
        }

        return row;
      })
    );
  };

  const onChangeRowValues = (value, name, row) => {
    setRows(
      rows.map((item) => {
        if (item.id === row.id) {
          return { ...item, [name]: value };
        }

        return item;
      })
    );
  };

  const onRevertChanges = () => {
    setRows(previousRows);
  };

  const getPayload = (row) => {
    if (!row.Value) {
      return null;
    }

    if (!!row.FromDate && !!row.ToDate) {
      const fromDate = new Date(row.FromDate);
      const toDate = new Date(row.ToDate);
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      if (fromDate.getTime() > toDate.getTime()) {
        return null;
      }

      return {
        key: row.Key,
        value: row.Value?.code ? row.Value.code : row.Value,
        fromDate: formatDate(row.FromDate),
        toDate: formatDate(row.ToDate),
      };
    }

    return null;
  };

  const onSaveSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);
    const payload = getPayload(row);
    if (!payload) {
      onRevertChanges();
      return;
    }

    const response = await AutomaticApprovalApiService.update(row.Key, payload);
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage));
      onRevertChanges();
      return;
    }

    await fetchSettingsAsync();
  };

  const onCreateSettingsAsync = async (row) => {
    const payload = getPayload(row);
    if (!payload) {
      return;
    }

    const response = await AutomaticApprovalApiService.add(payload);
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage));
      return;
    }

    await fetchSettingsAsync();
  };

  const onDeleteSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);
    const response = await AutomaticApprovalApiService.deleteByKey(row.Key);
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage));
      return;
    }

    await fetchSettingsAsync();
  };

  return (
    <>
      <Toolbar>
        <Typography variant="h6" id="tableTitle" component="div">
          {t("stops_to_remove")}
        </Typography>
        {!hasAddRow && !isReadOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onAddRow()} />}
        {hasAddRow && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertChanges()} />}
      </Toolbar>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns
                .filter((item) => !item.hidden)
                .map((column, key) => (
                  <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                    {column.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, key) => {
              return (
                <TableRow hover tabIndex={-1} key={key}>
                  {columns
                    .filter((item) => !item.hidden)
                    .map((column) => {
                      if (column.id === "Actions") {
                        return (
                          <TableCell key={100} align="left">
                            {!row.isEditMode && !hasEditRow && !hasAddRow && (
                              <>
                                <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onEditRow(row.id)} />
                                <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteSettingsAsync(row.id)} />
                              </>
                            )}
                            {row.isEditMode && (
                              <>
                                <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveSettingsAsync(row.id)} />
                                <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertChanges()} />
                              </>
                            )}
                            {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onCreateSettingsAsync(row)} />}
                          </TableCell>
                        );
                      }

                      const value = row[column.id];

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {(row.isEditMode || row.isAddMode) && column.editable ? (
                            <>
                              {column.type !== "date" && (
                                <MyDropdown
                                  onChange={(e, nextValue) => onChangeRowValues(nextValue, column.id, row)}
                                  value={value}
                                  options={stops}
                                />
                              )}
                              {column.type === "date" && <MyDatePicker value={value} onAccept={(date) => onChangeRowValues(date.toString(), column.id, row)} />}
                            </>
                          ) : column.format ? (
                            column.format(value)
                          ) : (
                            value
                          )}
                        </TableCell>
                      );
                    })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};